import * as THREE from 'three';
import { BROADSIDE_HALF_ARC, SHIP_CONFIG } from '../game/config';
import type { BattleEvent, BattleSnapshot, Faction, IslandDefinition, Quality, ShipKind, ShipState } from '../game/types';
import { Assets, fortressTemplate, lighthouseTemplate, palmTemplate, shipTemplate } from './models';
import { CombatEffects } from './combatEffects';
import { ShipPreview } from './preview';
import { IslandScenery } from './islands';
import { OceanWildlife } from './wildlife';

// Entirely static world-space pigment: a level ocean canvas, with no scrolling UVs or waves.
const WATER_VERTEX = `
varying vec3 world;
void main() {
  vec4 w = modelMatrix * vec4(position, 1.0);
  world = w.xyz;
  gl_Position = projectionMatrix * viewMatrix * w;
}`;
const WATER_FRAGMENT = `
uniform vec4 islands[12];
varying vec3 world;
float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float pigment(vec2 p) {
  vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1, 0)), f.x), mix(hash(i + vec2(0, 1)), hash(i + vec2(1)), f.x), f.y);
}
void main() {
  vec2 p = world.xz;
  float wash = pigment(p * .095) * .65 + pigment(p * .25 + 29.0) * .35;
  vec3 color = mix(vec3(.004, .022, .063), vec3(.014, .052, .13), wash);
  float fibers = sin(p.x * 20.0) * sin(p.y * 20.7);
  float visible = 1.0 - smoothstep(.3, 2.0, max(fwidth(p.x * 20.0), fwidth(p.y * 20.7)));
  color += vec3(.001, .0025, .005) * fibers * visible;
  float grain = pigment(p * 1.9) - .5;
  color += vec3(.003, .008, .018) * grain;
  // Broad, irregular brush marks stay attached to the chart, providing travel references.
  float brush = smoothstep(.62, .78, pigment(p * .18 + 71.0)) * (pigment(p * vec2(.4, 1.4)) - .3);
  color += vec3(.005, .016, .032) * brush;
  for (int i = 0; i < 12; i++) {
    if (islands[i].z > 0.0) {
      vec2 offset = p - islands[i].xy;
      float angle = atan(offset.x, offset.y), seed = islands[i].w;
      float outline = .965 + sin(angle * 3.0 + seed) * .035 + sin(angle * 5.0 + seed * .7) * .045 + cos(angle * 9.0 + seed) * .018;
      float coast = islands[i].z * outline * 1.025;
      float d = length(offset);
      float shore = 1.0 - smoothstep(coast, coast + 5.0, d);
      color = mix(color, vec3(.023, .145, .155), shore * .48);
      float edge = 1.0 - smoothstep(.1, .55, abs(d - coast - .3));
      color = mix(color, vec3(.13, .27, .29), edge * .25);
    }
  }
  float fog = smoothstep(280.0, 940.0, distance(cameraPosition, world));
  color = mix(color, vec3(.028, .07, .14), fog);
  gl_FragColor = vec4(color, 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`;

const WAKE_VERTEX = `
attribute float fade;
varying float foam;
void main() {
  foam = fade;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;
const WAKE_FRAGMENT = `
varying float foam;
void main() {
  gl_FragColor = vec4(.72, .89, 1.0, foam * .55);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`;

interface TrailPoint { x: number; z: number; nx: number; nz: number; width: number; born: number }
interface WakeTrail { mesh: THREE.Mesh; positions: Float32Array; fades: Float32Array; points: TrailPoint[] }
interface VisualShip { group: THREE.Group; sails: THREE.Object3D[]; flags: THREE.Object3D[]; trail: WakeTrail; sinkTime: number | null }
const MAX_TRAIL_POINTS = 180;

/** All state comes from simulation snapshots. No rendered coordinate feeds combat. */
export class OceanScene {
  private readonly assets = new Assets();
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(43, 1, .2, 1600);
  private readonly renderer: THREE.WebGLRenderer;
  private readonly sunlight: THREE.DirectionalLight;
  private readonly sea: THREE.Mesh<THREE.PlaneGeometry, THREE.ShaderMaterial>;
  private readonly templates = new Map<string, THREE.Group>();
  private readonly ships = new Map<number, VisualShip>();
  private readonly islands = new THREE.Group();
  private readonly scenery: IslandScenery;
  private readonly wildlife: OceanWildlife;
  private readonly preview: ShipPreview;
  private readonly wakeMaterial: THREE.ShaderMaterial;
  private readonly combatEffects: CombatEffects;
  private readonly arcs: THREE.Group;
  private readonly arcMaterials: THREE.MeshBasicMaterial[] = [];
  private readonly boundary: THREE.LineLoop;
  private readonly target = new THREE.Vector3(0, 0, 10);
  private quality: Quality;
  private time = 0;
  private zoomFactor = 1;
  private islandKey = '';
  private currentBattle: number | null = null;
  private lastBattleTime = -1;
  private battleSelection = '';
  private wasMenu = true;
  private disposed = false;
  private frameTotal = 0;
  private sampleTime = 0;
  private fps = 0;

  constructor(private readonly canvas: HTMLCanvasElement, quality: Quality) {
    this.quality = quality;
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: quality === 'standard', powerPreference: 'high-performance', alpha: false });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping; this.renderer.toneMappingExposure = 1.18;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.scene.background = new THREE.Color(0x0d2747); this.scene.fog = new THREE.Fog(0x0d2747, 260, 850);
    this.scene.add(new THREE.HemisphereLight(0xe4f4fc, 0x344453, 2.15));
    this.sunlight = new THREE.DirectionalLight(0xffe6b2, 2.8); this.sunlight.position.set(-70, 95, -65);
    this.sunlight.castShadow = true; this.sunlight.shadow.mapSize.set(1024, 1024);
    this.sunlight.shadow.camera.left = -75; this.sunlight.shadow.camera.right = 75; this.sunlight.shadow.camera.top = 75; this.sunlight.shadow.camera.bottom = -75;
    this.sunlight.shadow.camera.near = 1; this.sunlight.shadow.camera.far = 250; this.sunlight.shadow.bias = -.0006; this.sunlight.shadow.normalBias = .055;
    this.scene.add(this.sunlight, this.sunlight.target);
    const waterMaterial = this.assets.material(new THREE.ShaderMaterial({
      uniforms: { islands: { value: Array.from({ length: 12 }, () => new THREE.Vector4(0, 0, 0, 0)) } },
      vertexShader: WATER_VERTEX, fragmentShader: WATER_FRAGMENT,
    }));
    this.sea = new THREE.Mesh(this.assets.geometry(new THREE.PlaneGeometry(1800, 1800)), waterMaterial);
    this.sea.rotation.x = -Math.PI / 2; this.sea.position.y = -.3; this.scene.add(this.sea);
    this.wakeMaterial = this.assets.material(new THREE.ShaderMaterial({ vertexShader: WAKE_VERTEX, fragmentShader: WAKE_FRAGMENT, transparent: true, side: THREE.DoubleSide, depthWrite: false }));
    // Distinct templates share geometry and materials between every clone.
    for (const faction of ['player', 'enemy'] as Faction[]) for (const kind of ['sloop', 'frigate', 'galleon'] as ShipKind[]) this.templates.set(`${faction}-${kind}`, shipTemplate(this.assets, kind, faction));
    this.preview = new ShipPreview(this.renderer, this.assets, this.templates, document.querySelector<HTMLElement>('#ship-preview')!);
    this.scenery = new IslandScenery(this.assets, palmTemplate(this.assets), lighthouseTemplate(this.assets), fortressTemplate(this.assets));
    this.scene.add(this.islands);
    this.wildlife = new OceanWildlife(this.scene, this.assets, quality);
    this.combatEffects = new CombatEffects(this.scene, this.assets, quality);
    this.arcs = this.createArcs(); this.arcs.visible = false; this.scene.add(this.arcs);
    const border: number[] = []; for (let i = 0; i < 128; i++) { const angle = i / 128 * Math.PI * 2; border.push(Math.sin(angle) * 200, .1, Math.cos(angle) * 200); }
    const borderGeometry = this.assets.geometry(new THREE.BufferGeometry()); borderGeometry.setAttribute('position', new THREE.Float32BufferAttribute(border, 3));
    this.boundary = new THREE.LineLoop(borderGeometry, this.assets.material(new THREE.LineBasicMaterial({ color: 0xe4f5de, transparent: true, opacity: .26 }))); this.boundary.visible = false; this.scene.add(this.boundary);
    this.setQuality(quality); this.buildIslands([{ x: 45, z: -38, radius: 20, height: 11 }, { x: -62, z: -74, radius: 26, height: 14 }], false);
    this.resize();
  }

  private createShip(kind: ShipKind, faction: Faction): VisualShip {
    const group = this.templates.get(`${faction}-${kind}`)!.clone(true), sails: THREE.Object3D[] = [], flags: THREE.Object3D[] = [];
    group.traverse(child => { if (child.name === 'sail') sails.push(child); else if (child.name === 'flag') flags.push(child); });
    return { group, sails, flags, trail: this.createTrail(), sinkTime: null };
  }

  private createTrail(): WakeTrail {
    const positions = new Float32Array(MAX_TRAIL_POINTS * 9), fades = new Float32Array(MAX_TRAIL_POINTS * 3), indices: number[] = [];
    for (let i = 0; i < MAX_TRAIL_POINTS - 1; i++) { const a = i * 3; indices.push(a, a + 3, a + 1, a + 1, a + 3, a + 4, a + 1, a + 4, a + 2, a + 2, a + 4, a + 5); }
    const geometry = this.assets.geometry(new THREE.BufferGeometry());
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3).setUsage(THREE.DynamicDrawUsage));
    geometry.setAttribute('fade', new THREE.BufferAttribute(fades, 1).setUsage(THREE.DynamicDrawUsage)); geometry.setIndex(indices); geometry.setDrawRange(0, 0);
    const mesh = new THREE.Mesh(geometry, this.wakeMaterial); mesh.frustumCulled = false; mesh.renderOrder = 3; this.scene.add(mesh);
    return { mesh, positions, fades, points: [] };
  }

  private releaseShip(visual: VisualShip): void {
    this.scene.remove(visual.group, visual.trail.mesh); this.assets.removeGeometry(visual.trail.mesh.geometry); visual.trail.points.length = 0;
  }

  private clearShips(): void { this.ships.forEach(ship => this.releaseShip(ship)); this.ships.clear(); }

  private updateTrail(visual: VisualShip, state: ShipState, dt: number): void {
    const trail = visual.trail, lifetime = 8.5, length = SHIP_CONFIG[state.kind].length, heading = visual.group.rotation.y;
    while (trail.points.length && this.time - trail.points[0].born > lifetime) trail.points.shift();
    const x = visual.group.position.x - Math.sin(heading) * length * .47, z = visual.group.position.z - Math.cos(heading) * length * .47;
    const last = trail.points.at(-1);
    if (dt > 0 && state.alive && state.speed > .2 && (!last || Math.hypot(x - last.x, z - last.z) > (this.quality === 'low' ? 1.1 : .7))) {
      trail.points.push({ x, z, nx: Math.cos(heading), nz: -Math.sin(heading), width: .55 + state.speed / SHIP_CONFIG[state.kind].maxSpeed * .65, born: this.time });
      if (trail.points.length > MAX_TRAIL_POINTS) trail.points.shift();
    }
    trail.points.forEach((p, i) => {
      const age = this.time - p.born, width = p.width * (1 + age * .035);
      for (let j = 0; j < 3; j++) {
        const side = j - 1, index = i * 9 + j * 3;
        trail.positions[index] = p.x + p.nx * width * side; trail.positions[index + 1] = -.24; trail.positions[index + 2] = p.z + p.nz * width * side;
        trail.fades[i * 3 + j] = j === 1 ? Math.pow(Math.max(0, 1 - age / lifetime), 1.5) * Math.min(1, (age + .2) / .7) : 0;
      }
    });
    trail.mesh.visible = trail.points.length > 1; trail.mesh.geometry.setDrawRange(0, Math.max(0, trail.points.length - 1) * 12);
    trail.mesh.geometry.getAttribute('position').needsUpdate = true; trail.mesh.geometry.getAttribute('fade').needsUpdate = true;
  }

  private createArcs(): THREE.Group {
    const group = new THREE.Group();
    // A +z bow with +y up has port +x and starboard -x.
    for (const side of [1, -1]) {
      const positions: number[] = [], indices: number[] = [], outer = 94, inner = 10;
      for (let i = 0; i <= 40; i++) {
        const angle = side * Math.PI / 2 - BROADSIDE_HALF_ARC + i / 40 * BROADSIDE_HALF_ARC * 2;
        positions.push(Math.sin(angle) * inner, .05, Math.cos(angle) * inner, Math.sin(angle) * outer, .05, Math.cos(angle) * outer);
        if (i < 40) { const a = i * 2; indices.push(a, a + 2, a + 1, a + 1, a + 2, a + 3); }
      }
      const geometry = this.assets.geometry(new THREE.BufferGeometry()); geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3)); geometry.setIndex(indices);
      const material = this.assets.material(new THREE.MeshBasicMaterial({ color: side > 0 ? 0x87eff4 : 0xf6d88d, side: THREE.DoubleSide, transparent: true, opacity: .055, depthWrite: false }));
      this.arcMaterials.push(material); group.add(new THREE.Mesh(geometry, material));
      const edgePoints: THREE.Vector3[] = [];
      for (let i = 0; i <= 40; i++) { const angle = side * Math.PI / 2 - BROADSIDE_HALF_ARC + i / 40 * BROADSIDE_HALF_ARC * 2; edgePoints.push(new THREE.Vector3(Math.sin(angle) * outer, .15, Math.cos(angle) * outer)); }
      const line = new THREE.Line(this.assets.geometry(new THREE.BufferGeometry().setFromPoints(edgePoints)), this.assets.material(new THREE.LineBasicMaterial({ color: side > 0 ? 0xaffcff : 0xffe1a1, transparent: true, opacity: .3 }))); group.add(line);
    }
    return group;
  }

  private buildIslands(definitions: IslandDefinition[], isFortress: boolean): void {
    const shores = this.sea.material.uniforms.islands.value as THREE.Vector4[];
    shores.forEach(value => value.set(0, 0, 0, 0));
    definitions.forEach((island, index) => shores[index]?.set(island.x, island.z, island.radius, index * .7));
    this.scenery.rebuild(this.islands, definitions, isFortress);
    this.wildlife.setMap(definitions);
  }

  render(snapshot: BattleSnapshot | null, delta: number): void {
    if (this.disposed) return;
    const dt = Math.min(Math.max(delta, 0), .05), menu = snapshot === null;
    this.frameTotal++; this.sampleTime += Math.max(0, delta);
    if (this.sampleTime >= .7) { this.fps = Math.round(this.frameTotal / this.sampleTime); this.frameTotal = 0; this.sampleTime = 0; }
    const animating = menu || snapshot.phase !== 'paused';
    if (animating) this.time += dt;
    if (menu) {
      if (!this.wasMenu) {
        this.clearShips(); this.combatEffects.clear(); this.currentBattle = null;
        this.buildIslands([{ x: 45, z: -38, radius: 20, height: 11 }, { x: -62, z: -74, radius: 26, height: 14 }], false); this.islandKey = '';
      }
      this.arcs.visible = false; this.boundary.visible = false;
      this.target.lerp(new THREE.Vector3(0, 0, 10), this.wasMenu ? .04 : 1);
      this.camera.position.copy(this.target).add(new THREE.Vector3(28, 30, 40));
    } else {
      this.boundary.visible = true;
      const selection = `${snapshot.options.ship}:${snapshot.options.map}:${snapshot.options.difficulty}:${snapshot.options.seed ?? ''}`;
      if (this.wasMenu || this.currentBattle !== snapshot.playerId || snapshot.time < this.lastBattleTime || selection !== this.battleSelection) {
        this.clearShips(); this.combatEffects.clear(); this.wildlife.reset(); this.currentBattle = snapshot.playerId;
      }
      this.lastBattleTime = snapshot.time; this.battleSelection = selection;
      const key = `${snapshot.options.map}:${snapshot.islands.map(i => `${i.x},${i.z},${i.radius}`).join(';')}`;
      if (key !== this.islandKey) { this.buildIslands(snapshot.islands, snapshot.options.map === 'fortress'); this.islandKey = key; }
      const knownIds = new Set<number>();
      snapshot.ships.forEach(state => {
        knownIds.add(state.id); let visual = this.ships.get(state.id);
        if (!visual) { visual = this.createShip(state.kind, state.faction); visual.group.position.set(state.x, .15, state.z); visual.group.rotation.y = state.heading; this.ships.set(state.id, visual); this.scene.add(visual.group); }
        this.updateShip(visual, state, snapshot, animating ? dt : 0);
      });
      this.ships.forEach((visual, id) => { if (!knownIds.has(id)) { this.releaseShip(visual); this.ships.delete(id); } });
      const player = snapshot.ships.find(ship => ship.id === snapshot.playerId);
      if (player) {
        const follow = new THREE.Vector3(player.x, 0, player.z + 19);
        this.target.lerp(follow, this.wasMenu ? 1 : animating ? 1 - Math.exp(-dt * 6.5) : 0);
        this.arcs.visible = player.alive; this.arcs.position.set(player.x, .35, player.z); this.arcs.rotation.y = player.heading; this.arcs.scale.setScalar(SHIP_CONFIG[player.kind].range / 94);
        this.arcMaterials[0].opacity = player.portCooldown > 0 ? .022 : .06;
        this.arcMaterials[1].opacity = player.starboardCooldown > 0 ? .022 : .06;
      }
      this.camera.position.copy(this.target).add(new THREE.Vector3(65, 85, 80).multiplyScalar(this.zoomFactor));
    }
    this.camera.lookAt(this.target);
    this.sunlight.position.copy(this.target).add(new THREE.Vector3(-70, 95, -65)); this.sunlight.target.position.copy(this.target);
    this.combatEffects.update(snapshot, animating ? dt : 0);
    this.wildlife.update(animating ? dt : 0, this.target);
    this.wasMenu = menu; this.renderer.info.autoReset = false; this.renderer.info.reset(); this.renderer.render(this.scene, this.camera);
    if (menu) this.preview.render();
  }

  private animateSails(visual: VisualShip, time: number, wind: number): void {
    visual.sails.forEach((sail, i) => { sail.rotation.y = Math.sin(time * 1.1 + i) * .009 * wind; });
    visual.flags.forEach((flag, i) => { flag.rotation.y = Math.sin(time * 3.4 + i) * .12; flag.rotation.z = Math.sin(time * 2.9 + i) * .035; });
  }

  private updateShip(visual: VisualShip, state: ShipState, snapshot: BattleSnapshot, dt: number): void {
    // Visual interpolation follows the fixed-step state, never modifies it.
    const group = visual.group, blend = dt === 0 ? 1 : 1 - Math.exp(-dt * 45);
    group.position.x += (state.x - group.position.x) * blend; group.position.z += (state.z - group.position.z) * blend;
    group.rotation.y += Math.atan2(Math.sin(state.heading - group.rotation.y), Math.cos(state.heading - group.rotation.y)) * blend;
    group.position.y = .16; group.rotation.z = 0; group.rotation.x = 0;
    this.animateSails(visual, this.time + state.id, snapshot.wind.strength);
    if (!state.alive && state.sunkAt !== null) {
      visual.sinkTime = (visual.sinkTime ?? Math.max(0, snapshot.time - state.sunkAt)) + dt;
      const elapsed = Math.max(snapshot.time - state.sunkAt, visual.sinkTime);
      group.position.y -= Math.min(14, elapsed * 1.9); group.rotation.z += Math.min(.8, elapsed * .18);
      group.visible = elapsed < 7;
    } else { group.visible = true; visual.sinkTime = null; }
    this.updateTrail(visual, state, dt);
  }

  effects(events: BattleEvent[]): void {
    events.forEach(event => {
      if (event.type === 'result') return;
      const group = this.ships.get(event.shipId)?.group;
      const kind = group?.name.split('-').pop() as ShipKind | undefined;
      const pose = group && kind && kind in SHIP_CONFIG ? { kind, x: group.position.x, y: group.position.y, z: group.position.z, heading: group.rotation.y } : undefined;
      this.combatEffects.emit(event, pose);
    });
  }

  setQuality(quality: Quality): void {
    this.quality = quality; this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, quality === 'low' ? 1 : 1.5));
    this.renderer.shadowMap.enabled = quality === 'standard'; this.sunlight.castShadow = quality === 'standard';
    this.combatEffects.setQuality(quality);
    this.wildlife.setQuality(quality);
    this.preview.setQuality(quality);
    this.resize();
  }

  selectPreview(kind: ShipKind): void { this.preview.select(kind); }
  rotatePreview(delta: number): void { this.preview.rotate(delta); }
  zoomPreview(delta: number): void { this.preview.zoom(delta); }
  resetPreview(): void { this.preview.reset(); }
  getShipThumbnails(): Record<ShipKind, string> { return this.preview.thumbnails(); }
  getPreviewState(): { kind: ShipKind; yaw: number; zoom: number } { return this.preview.state(); }

  zoom(delta: number): void { this.zoomFactor = THREE.MathUtils.clamp(this.zoomFactor + delta * .00065, .58, 1.45); }

  resize(): void {
    const width = Math.max(1, this.canvas.clientWidth || window.innerWidth), height = Math.max(1, this.canvas.clientHeight || window.innerHeight);
    this.camera.aspect = width / height; this.camera.updateProjectionMatrix(); this.renderer.setSize(width, height, false);
  }

  getStats(): { fps: number; drawCalls: number; triangles: number; geometries: number; textures: number } & ReturnType<CombatEffects['getStats']> & ReturnType<OceanWildlife['getStats']> {
    return { fps: this.fps, drawCalls: this.renderer.info.render.calls, triangles: this.renderer.info.render.triangles, geometries: this.renderer.info.memory.geometries, textures: this.renderer.info.memory.textures, ...this.combatEffects.getStats(), ...this.wildlife.getStats() };
  }

  dispose(): void {
    if (this.disposed) return; this.disposed = true;
    this.scene.clear(); this.ships.clear(); this.templates.clear(); this.combatEffects.dispose(); this.wildlife.dispose();
    this.preview.dispose(); this.sunlight.shadow.map?.dispose(); this.assets.dispose(); this.renderer.dispose();
  }
}
